import React, {FC, useState} from 'react'
import {KanbanCard} from './KanbanCard'
import {TaskModal} from '../../../../../_metronic/partials/modals/task/TaskModal'
import {Task} from '../../core/_models'

type Props = {
  id: string
  title: string
  color: string
  tasks: Task[]
  columnIndex: number
  onDragStart: (task: Task, columnId: string, taskIndex: number) => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (columnId: string, index?: number) => void
  onTaskReorder: (columnId: string, dropIndex: number) => void
  onEditName: (columnId: string, newName: string) => void
  onColumnDragStart: (column: Column, index: number) => void
  onColumnDragOver: (e: React.DragEvent) => void
  onColumnDrop: (index: number) => void
  onColumnDragEnd: () => void
  isDragging: boolean
}

interface Column {
  id: string
  title: string
  color: string
  tasks: Task[]
}

const KanbanLane: FC<Props> = ({
  id,
  title,
  color,
  tasks,
  columnIndex,
  onDragStart,
  onDragOver,
  onDrop,
  onTaskReorder,
  onEditName,
  onColumnDragStart,
  onColumnDragOver,
  onColumnDrop,
  onColumnDragEnd,
  isDragging,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(title)
  const [activeDropIndex, setActiveDropIndex] = useState<number | null>(null)
  const [showTaskModal, setShowTaskModal] = useState(false)

  const getColorClass = (color: string) => {
    switch (color) {
      case 'backlog':
        return 'border-warning'
      case 'in-progress':
        return 'border-primary'
      case 'done':
        return 'border-success'
      default:
        return 'border-gray-300'
    }
  }

  const getHeaderColor = (color: string) => {
    switch (color) {
      case 'backlog':
        return 'text-white'
      case 'in-progress':
        return 'text-white'
      case 'done':
        return 'text-white'
      case 'custom-gray':
        return 'text-white'
      default:
        return 'text-gray-600'
    }
  }

  const getHeaderBackground = (color: string) => {
    switch (color) {
      case 'backlog':
        return '#f6c000'
      case 'in-progress':
        return '#1b84ff'
      case 'done':
        return '#17c653'
      case 'custom-gray':
        return '#92929f'
      default:
        return '#f8f9fa'
    }
  }

  const handleEditClick = () => {
    setIsEditing(true)
    setEditName(title)
  }

  const handleEditSave = () => {
    if (editName.trim() && editName !== title) {
      onEditName(id, editName)
    }
    setIsEditing(false)
  }

  const handleEditCancel = () => {
    setEditName(title)
    setIsEditing(false)
  }

  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditSave()
    } else if (e.key === 'Escape') {
      handleEditCancel()
    }
  }

  const handleTaskDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleTaskDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    onTaskReorder(id, dropIndex)
  }

  return (
    <div className={`kanban-demo-column vh-75 ${isDragging ? 'dragging' : ''}`} data-color={color}>
      <div
        className={`card border-2 overflow-y-auto overflow-x-hidden ${getColorClass(color)} h-100`}
        onDragOver={onDragOver}
        onDrop={() => {
          onDrop(id)
          setActiveDropIndex(null)
        }}
      >
        <div
          className={`card-header ${getHeaderColor(color)} py-2 ${isDragging ? 'opacity-50' : ''}`}
          // draggable
          // onDragStart={() => onColumnDragStart({id, title, color, tasks} as Column, columnIndex)}
          // onDragOver={onColumnDragOver}
          // onDrop={() => onColumnDrop(columnIndex)}
          // onDragEnd={onColumnDragEnd}
          style={{
            cursor: 'default',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: getHeaderBackground(color),
          }}
        >
          <div className='d-flex align-items-center'>
            <div className='d-flex align-items-center flex-grow-1'>
              {isEditing ? (
                <input
                  type='text'
                  className='form-control form-control-sm border-0 bg-transparent fw-bold p-0'
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={handleEditKeyPress}
                  onBlur={handleEditSave}
                  autoFocus
                  style={{
                    fontSize: '0.9rem',
                    minWidth: '80px',
                    color: '#495057',
                  }}
                />
              ) : (
                <h5
                  className='card-title mb-0 fw-bold'
                  onClick={color === 'custom-gray' ? handleEditClick : undefined}
                  style={{
                    cursor: color === 'custom-gray' ? 'pointer' : 'default',
                    userSelect: color === 'custom-gray' ? 'none' : 'auto',
                  }}
                  title={
                    color === 'custom-gray' ? 'Clique para editar o nome da coluna' : undefined
                  }
                >
                  {title}
                </h5>
              )}
            </div>
            <span className='badge kanban-count-badge rounded-pill ms-auto'>
              {tasks.length}
            </span>
          </div>
        </div>
        <div className='card-body p-4'>
          {/* Link Adicionar Tarefa */}
          <div className='text-center mb-3'>
            <button
              className='btn btn-link text-muted p-0 border-0 text-decoration-none'
              title='Adicionar tarefa'
              style={{fontSize: '0.875rem'}}
              onClick={() => setShowTaskModal(true)}
            >
              <i className='bi bi-plus-circle me-1'></i>
              Adicionar tarefa
            </button>
          </div>

          <div className='kanban-demo-tasks'>
            {/* Zona de drop no topo da coluna */}
            <div
              className='drop-zone drop-zone-top'
              onDragOver={handleTaskDragOver}
              onDrop={(e) => handleTaskDrop(e, 0)}
              style={{
                height: '8px',
                marginBottom: '8px',
                backgroundColor: 'transparent',
                border: '2px dashed transparent',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
              }}
            />

            {tasks.map((task, index) => (
              <React.Fragment key={task.id}>
                <KanbanCard
                  task={task}
                  showDeliveryFooter={id === 'entregue'}
                  onDragStart={() => onDragStart(task, id, index)}
                  onDragOver={(e) => {
                    e.preventDefault()
                    setActiveDropIndex(index + 1) // ativa a drop-zone abaixo do card
                  }}
                  onDragLeave={() => {
                    setActiveDropIndex(null)
                  }}
                />
                {/* Zona de drop entre tarefas */}
                <div
                  className={`drop-zone drop-zone-between ${
                    activeDropIndex === index + 1 ? 'active' : ''
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault()
                    setActiveDropIndex(index + 1) // ativa a drop-zone abaixo do card
                  }}
                  onDragLeave={() => {
                    setActiveDropIndex(null)
                  }}
                  onDrop={(e) => handleTaskDrop(e, index + 1)}
                  style={{
                    height: '8px',
                    margin: '8px 0',
                    backgroundColor: 'transparent',
                    border: '2px dashed transparent',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                  }}
                />
              </React.Fragment>
            ))}

            {tasks.length === 0 && (
              <div className='text-center text-muted py-8'>
                <i className='bi bi-inbox fs-2x'></i>
                <p className='mt-2 mb-0'>Nenhuma tarefa</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <TaskModal show={showTaskModal} handleClose={() => setShowTaskModal(false)} stageId={id} />
    </div>
  )
}

export {KanbanLane}
